/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import {
  Package, Plus, Edit3, Trash2, Copy, Users, CreditCard, Wallet, Coins,
  TrendingUp, Shield, Building, Briefcase, Eye, UserPlus, Check, X,
  Loader2, Search, Filter, BarChart3, Settings, Zap, Star, Crown,
  ChevronRight, ArrowRight, Sparkles, Target, Award, Clock, Calendar,
  PieChart, Activity, Info, AlertCircle, CheckCircle2, RefreshCw, ShoppingCart, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import StripeCheckoutButton from './StripeCheckoutButton';
import { getToken, getAuthHeader as getStoredAuthHeader } from '../services/tokenService';
import InstitutionalHero from './ui/InstitutionalHero';


export const GestionPlansAssignDialog = ({ ctx }) => {
  const {
    activeTab, assignForm, assignPlanSearch, assignUserSearch, assignmentSearch,
    assignmentStatusFilter, assignments, dialogStep, editingPlan, filterRole, filterType,
    loading, planForm, plans, saving, searchTerm, selectedPlan, selectedPlanIds,
    showAssignDialog, showDetailDialog, showPlanDialog, users,
    setActiveTab, setAssignForm, setAssignPlanSearch, setAssignUserSearch, setAssignmentSearch,
    setAssignmentStatusFilter, setAssignments, setDialogStep, setEditingPlan, setFilterRole,
    setFilterType, setLoading, setPlanForm, setPlans, setSaving, setSearchTerm, setSelectedPlan,
    setSelectedPlanIds, setShowAssignDialog, setShowDetailDialog, setShowPlanDialog, setUsers,
    fetchAssignments, fetchPlans, fetchUsers, filteredPlans, getAuthHeader, getPricingTypeConfig,
    getRoleConfig, handleAssignPlan, handleBulkDeletePlans, handleBulkToggleActive, handleDeletePlan,
    handleDuplicatePlan, handleSavePlan, openCreateDialog, openDetailDialog, openEditDialog,
    renderPlanPrice, stats, togglePlanSelection, toggleSelectAllPlans,
    PRICING_TYPES, TARGET_ROLES, DEFAULT_PLAN, PLAN_COLORS, getErrorMessage,
  } = ctx;
  return (
      <Dialog open={showAssignDialog} onOpenChange={(open) => {
        setShowAssignDialog(open);
        if (!open) { setAssignUserSearch(''); setAssignPlanSearch(''); setAssignForm({ user_id: '', plan_id: '' }); }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-blue-600" />
              </div>
              Assigner un plan
            </DialogTitle>
            <DialogDescription>
              Attribuez un plan d&apos;abonnement à un utilisateur
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* === Recherche dynamique d'utilisateur === */}
            <div>
              <Label className="text-sm font-medium">Utilisateur *</Label>
              <Input
                placeholder="Rechercher par nom, prénom ou email…"
                value={assignUserSearch}
                onChange={(e) => {
                  setAssignUserSearch(e.target.value);
                  if (assignForm.user_id) setAssignForm({ ...assignForm, user_id: '' });
                }}
                className="mt-1.5"
                data-testid="assign-user-search"
                autoComplete="off"
              />
              {assignUserSearch && !assignForm.user_id && (
                <div className="mt-2 max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y" data-testid="assign-user-results">
                  {(() => {
                    const q = assignUserSearch.toLowerCase().trim();
                    const matches = (users || []).filter((u) => (
                      (u.email || '').toLowerCase().includes(q) ||
                      (u.nom || '').toLowerCase().includes(q) ||
                      (u.prenom || '').toLowerCase().includes(q)
                    )).slice(0, 50);
                    if (matches.length === 0) {
                      return <div className="px-3 py-3 text-sm text-slate-500 text-center">Aucun utilisateur trouvé</div>;
                    }
                    return matches.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => {
                          setAssignForm({ ...assignForm, user_id: u.id });
                          setAssignUserSearch(`${u.prenom || ''} ${u.nom || ''} (${u.email || ''})`.trim());
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center gap-3"
                        data-testid={`assign-user-result-${u.id}`}
                      >
                        <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700 flex-shrink-0">
                          {(u.prenom || '?')[0]}{(u.nom || '?')[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-slate-800 truncate">{u.prenom} {u.nom}</div>
                          <div className="text-xs text-slate-500 truncate">{u.email}{u.role ? ` — ${u.role}` : ''}</div>
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              )}
              {assignForm.user_id && (
                <div className="mt-1 flex items-center justify-between p-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="assign-user-selected">
                  <div className="flex items-center gap-2 text-sm text-emerald-800">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="font-medium">Utilisateur sélectionné</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setAssignForm({ ...assignForm, user_id: '' }); setAssignUserSearch(''); }}
                    className="text-xs text-emerald-700 hover:text-emerald-900 underline"
                  >
                    Changer
                  </button>
                </div>
              )}
            </div>

            {/* === Recherche dynamique de plan === */}
            <div>
              <Label className="text-sm font-medium">Plan *</Label>
              <Input
                placeholder="Rechercher un plan par nom ou type…"
                value={assignPlanSearch}
                onChange={(e) => {
                  setAssignPlanSearch(e.target.value);
                  if (assignForm.plan_id) setAssignForm({ ...assignForm, plan_id: '' });
                }}
                className="mt-1.5"
                data-testid="assign-plan-search"
                autoComplete="off"
              />
              {assignPlanSearch && !assignForm.plan_id && (
                <div className="mt-2 max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y" data-testid="assign-plan-results">
                  {(() => {
                    const q = assignPlanSearch.toLowerCase().trim();
                    const matches = plans.filter((p) => {
                      if (!p.is_active) return false;
                      const tcfg = getPricingTypeConfig(p.type_tarification);
                      return (
                        (p.nom || '').toLowerCase().includes(q) ||
                        (p.description || '').toLowerCase().includes(q) ||
                        (tcfg.label || '').toLowerCase().includes(q)
                      );
                    }).slice(0, 50);
                    if (matches.length === 0) {
                      return <div className="px-3 py-3 text-sm text-slate-500 text-center">Aucun plan actif trouvé</div>;
                    }
                    return matches.map((p) => {
                      const tcfg = getPricingTypeConfig(p.type_tarification);
                      const Icon = tcfg.icon;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setAssignForm({ ...assignForm, plan_id: p.id });
                            setAssignPlanSearch(`${p.nom} — ${tcfg.label}`);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors flex items-center gap-3"
                          data-testid={`assign-plan-result-${p.id}`}
                        >
                          <div className="p-1.5 rounded-md flex-shrink-0" style={{ backgroundColor: `${p.couleur}20` }}>
                            <Icon className="h-4 w-4" style={{ color: p.couleur }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-800 truncate">{p.nom}</div>
                            <div className="text-xs text-slate-500 truncate flex items-center gap-2">
                              <Badge variant="outline" className={`${tcfg.bg} ${tcfg.text} border-0 text-[10px] py-0`}>{tcfg.label}</Badge>
                              {p.pricing?.prix_mensuel > 0 && <span>· {p.pricing.prix_mensuel}€/mois</span>}
                            </div>
                          </div>
                        </button>
                      );
                    });
                  })()}
                </div>
              )}
              {assignForm.plan_id && (
                <div className="mt-1 flex items-center justify-between p-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="assign-plan-selected">
                  <div className="flex items-center gap-2 text-sm text-emerald-800">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="font-medium">Plan sélectionné</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setAssignForm({ ...assignForm, plan_id: '' }); setAssignPlanSearch(''); }}
                    className="text-xs text-emerald-700 hover:text-emerald-900 underline"
                  >
                    Changer
                  </button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleAssignPlan} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};

export default GestionPlansAssignDialog;
